import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Crown, Quote } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { StarRating } from "../components/StarRating";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";

export default function Reviews() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState(null);
  const [summary, setSummary] = useState({ average: 0, count: 0 });
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/reviews").then((r) => setReviews(r.data.reviews)).catch(() => setReviews([]));
    api.get("/reviews/summary").then((r) => setSummary(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    if (user) api.get("/reviews/mine").then((r) => {
      if (r.data.review) { setRating(r.data.review.rating); setText(r.data.review.text); }
    }).catch(() => {});
  }, [user]);

  const submit = async () => {
    if (!rating) { toast.error("Please choose a star rating."); return; }
    setSaving(true);
    try {
      await api.post("/reviews", { rating, text });
      toast.success("Thank you for your review!");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />
      <div className="mx-auto max-w-5xl px-6 py-12">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold"><Crown className="h-4 w-4" /> Client Reviews</span>
            <h1 className="mt-2 font-heading text-4xl text-cream md:text-5xl">What the crowned say</h1>
          </div>
          <div className="rounded-xl border border-charcoal-border bg-charcoal-surface px-6 py-4 text-center" data-testid="reviews-summary">
            <p className="font-heading text-4xl text-gold">{summary.average || "—"}<span className="text-xl text-cream/50"> / 5</span></p>
            <StarRating value={Math.round(summary.average)} size={16} />
            <p className="mt-1 text-xs text-muted-foreground">{summary.count} review{summary.count === 1 ? "" : "s"}</p>
          </div>
        </div>

        {user && (
          <div className="mt-10 rounded-2xl border border-charcoal-border bg-charcoal-surface p-6 md:p-8" data-testid="leave-review-card">
            <h2 className="font-heading text-2xl text-cream">Leave a review</h2>
            <p className="mb-4 text-sm text-muted-foreground">Available to members who have completed a scalp scan.</p>
            <StarRating value={rating} onChange={setRating} size={26} testid="review-rating-input" />
            <Textarea
              value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Share your experience with Carlocs…"
              className="mt-4 border-charcoal-border bg-charcoal text-cream focus-visible:ring-gold"
              data-testid="review-text-input"
            />
            <Button onClick={submit} disabled={saving} className="mt-4 rounded-full bg-gold text-charcoal hover:bg-gold/90" data-testid="submit-review-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit review"}
            </Button>
          </div>
        )}

        <div className="mt-12">
          {reviews === null ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
          ) : reviews.length === 0 ? (
            <p className="text-center text-muted-foreground" data-testid="reviews-empty">No reviews yet — be the first to share.</p>
          ) : (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="reviews-list">
              {reviews.map((rv, i) => (
                <motion.div
                  key={rv.id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="rounded-xl border border-charcoal-border bg-charcoal-surface p-6"
                >
                  <Quote className="mb-3 h-5 w-5 text-gold/60" />
                  <p className="text-sm leading-relaxed text-cream/90">{rv.text || "Highly recommend."}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-cream">{rv.name}</p>
                      {rv.verified && <span className="text-[10px] uppercase tracking-[0.2em] text-gold">Verified member</span>}
                    </div>
                    <StarRating value={rv.rating} size={14} />
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
